/**
 * useQueryTechnologyAssets Hook
 *
 * Specialized hook for fetching and filtering assets impacted by a technology.
 * Manages filter state, builds graph queries, and returns paginated data.
 *
 * Query Pattern: Asset -> HAS_PORT -> Port -> HAS_TECHNOLOGY -> Technology
 *
 * Risk Level: Low
 * - Uses established useMy hook for data fetching
 * - Filter state persisted via useStorage
 * - No side effects beyond data fetching
 */

import React, { useMemo } from 'react';
import { useDebounce } from 'use-debounce';

import { surfaceFilters } from '@/app/constant';
import { combineAssetNodeResults } from '@/hooks/useAssets';
import { useMy } from '@/hooks/useMy';
import { createPortNodeFilter } from '@/sections/insights/queryBuilder/legacy/utils/portRelationships';
import { Asset, AssetStatus, NodeLabel, Operator, Port } from '@/types';
import { QueryStatus } from '@/utils/api';
import { buildAssetStatusFilter } from '@/utils/entities/assetStatusQuery.util';
import { StorageKey, useStorage } from '@/utils/storage/useStorage.util';

/**
 * Type for an asset with its associated port information
 */
export interface ImpactedAssetPort {
  asset: Asset;
  port: Port;
  visited: string;
}

/**
 * Filter state for technology assets
 *
 * NOTE: This type is designed to be compatible with the Filters type from useAssets
 * so that we can use the shared AssetTableFilters component.
 * All filter values are strings (including port) for consistency.
 */
export interface TechnologyAssetsFilters {
  search?: string;
  advancedFilters?: {
    status?: AssetStatus[];
    tags?: string[];
    attackSurface?: string[];
    protocol?: string[];
    port?: string[]; // String array for compatibility with AssetTableFilters
  };
  sort?: {
    orderBy: keyof Asset;
    descending: boolean;
  };
}

/**
 * Parameters for useQueryTechnologyAssets hook
 */
interface UseQueryTechnologyAssetsParams {
  /** The technology key to filter assets by */
  technologyKey: string;
  /** Whether the query is enabled */
  enabled?: boolean;
}

/**
 * Return type for useQueryTechnologyAssets hook
 */
interface UseQueryTechnologyAssetsReturn {
  /** Array of impacted asset-port pairs */
  data: ImpactedAssetPort[];
  /** Current filter state */
  filters: TechnologyAssetsFilters;
  /** Function to update filters */
  setFilters: React.Dispatch<React.SetStateAction<TechnologyAssetsFilters>>;
  /** Function to reset filters to defaults */
  resetFilters: () => void;
  /** Query status */
  status: QueryStatus;
  /** Query error */
  error: Error | null;
  /** Fetch next page function */
  fetchNextPage: () => void;
  /** Whether there's a next page */
  hasNextPage: boolean;
  /** Whether currently fetching (any page) - needed for keepPreviousData empty state handling */
  isFetching: boolean;
  /** Whether currently fetching next page */
  isFetchingNextPage: boolean;
  /** Function to invalidate the query */
  invalidate: () => void;
}

/**
 * Default filter state
 *
 * NOTE: No default status filter - unlike main asset tables, the drawer context
 * should show ALL assets where this technology is detected, regardless of status.
 * User explicitly asked "show me assets impacted by this technology" = ALL assets.
 * Status filter is available via dropdown for users who want to filter further.
 */
const defaultFilters: TechnologyAssetsFilters = {
  advancedFilters: {},
  sort: {
    orderBy: 'identifier',
    descending: false,
  },
  search: '',
};

/**
 * Build asset-level filters for the graph query
 * Uses proper filter transformations matching QueryBuilder patterns
 */
function buildAssetFilters(
  filters: TechnologyAssetsFilters,
  debouncedSearch?: string
): any[] {
  const result: any[] = [];

  // Status filter - use buildAssetStatusFilter() for proper timestamp logic
  if (
    filters.advancedFilters?.status &&
    filters.advancedFilters.status.length > 0
  ) {
    const statusFilter = buildAssetStatusFilter(filters.advancedFilters.status);
    if (statusFilter) {
      result.push(statusFilter);
    }
  }

  // Tags filter - MUST use reverseOperands: true for array field
  // Each tag becomes a separate filter with single value
  if (
    filters.advancedFilters?.tags &&
    filters.advancedFilters.tags.length > 0
  ) {
    const tagFilters = filters.advancedFilters.tags.map(tag => ({
      field: 'tags',
      operator: 'IN' as Operator,
      value: tag, // Single tag value, not array
      reverseOperands: true, // Required for IN operator with array fields
    }));

    if (tagFilters.length === 1) {
      result.push(tagFilters[0]);
    } else {
      // Multiple tags - use OR logic
      result.push({
        field: '',
        operator: 'OR',
        value: tagFilters,
      });
    }
  }

  // Attack Surface filter - use surfaceFilters constant for complex logic
  if (
    filters.advancedFilters?.attackSurface &&
    filters.advancedFilters.attackSurface.length > 0
  ) {
    const surfaceFilterStructures = filters.advancedFilters.attackSurface
      .map(val => surfaceFilters[val])
      .filter(Boolean);

    if (surfaceFilterStructures.length === 1) {
      result.push(surfaceFilterStructures[0]);
    } else if (surfaceFilterStructures.length > 1) {
      // Multiple attack surfaces - use OR logic
      result.push({
        field: '',
        operator: 'OR',
        value: surfaceFilterStructures,
      });
    }
  }

  // Search filter - search across identifier and group fields
  // Searches both IP address (identifier) and domain name (group)
  if (debouncedSearch && debouncedSearch.trim()) {
    const searchTerm = debouncedSearch.trim();

    // Create filters for both fields
    const identifierFilter = {
      field: 'identifier',
      operator: 'CONTAINS' as Operator,
      value: searchTerm,
    };

    const groupFilter = {
      field: 'group',
      operator: 'CONTAINS' as Operator,
      value: searchTerm,
    };

    // Combine with OR logic to search both fields
    result.push({
      field: '',
      operator: 'OR',
      value: [identifierFilter, groupFilter],
    });
  }

  return result;
}

/**
 * Build port-level filters for the graph query
 * These filters apply to the Port node in the relationship chain
 *
 * Uses createPortNodeFilter from portRelationships.ts for consistent:
 * - Field mapping: "protocol" → "service", "port" → "port"
 * - Operator normalization: "IN" → "=" for Neo4j compatibility
 * - Type conversion: port values to numbers
 */
function buildPortFilters(filters: TechnologyAssetsFilters): any[] {
  const result: any[] = [];

  // Protocol filter - uses createPortNodeFilter for field mapping (protocol → service)
  // and operator normalization (IN → =)
  if (
    filters.advancedFilters?.protocol &&
    filters.advancedFilters.protocol.length > 0
  ) {
    result.push(
      createPortNodeFilter('protocol', '=', filters.advancedFilters.protocol)
    );
  }

  // Port number filter - uses createPortNodeFilter for type conversion (to numbers)
  // and operator normalization (IN → =)
  if (
    filters.advancedFilters?.port &&
    filters.advancedFilters.port.length > 0
  ) {
    result.push(
      createPortNodeFilter('port', '=', filters.advancedFilters.port)
    );
  }

  return result;
}

/**
 * Custom hook for querying assets impacted by a specific technology
 *
 * @example
 * ```tsx
 * const {
 *   data,
 *   filters,
 *   setFilters,
 *   status,
 *   fetchNextPage,
 *   hasNextPage,
 * } = useQueryTechnologyAssets({
 *   technologyKey: '#technology#...',
 *   enabled: true,
 * });
 * ```
 */
export function useQueryTechnologyAssets({
  technologyKey,
  enabled = true,
}: UseQueryTechnologyAssetsParams): UseQueryTechnologyAssetsReturn {
  // Manage filter state with localStorage persistence
  // Uses separate storage key to avoid collision with main Technology table
  const [filters, setFilters, resetFilters] =
    useStorage<TechnologyAssetsFilters>(
      {
        queryKey: 'technologyAssetsFiltersV1',
        key: StorageKey.FILTERS_TECHNOLOGY_ASSETS,
      },
      defaultFilters
    );

  // Debounce search input to reduce API calls (300ms when typing)
  const searchValue = filters.search || '';
  const [debouncedSearch] = useDebounce(searchValue, 300);

  // BUGFIX: Bypass debounce when clearing search for synchronous query key update
  // useDebounce with 0ms is still async (uses setTimeout), causing stale query keys
  // When search is cleared, use empty string immediately to trigger refetch
  const effectiveSearch = searchValue === '' ? '' : debouncedSearch;

  // Build asset filters for the query
  const assetFilters = useMemo(
    () => buildAssetFilters(filters, effectiveSearch),
    [filters, effectiveSearch]
  );

  // Build port filters for the query
  const portFilters = useMemo(() => buildPortFilters(filters), [filters]);

  // Query: Asset -> HAS_PORT -> Port -> HAS_TECHNOLOGY -> Technology
  const {
    data: impactedAssetsTree,
    status,
    error,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    invalidate,
  } = useMy(
    {
      resource: 'asset',
      tree: true,
      query: {
        node: {
          labels: ['Asset'] as NodeLabel[],
          // Apply asset-level filters
          ...(assetFilters.length > 0 && { filters: assetFilters }),
          relationships: [
            {
              label: 'HAS_PORT' as const,
              target: {
                labels: ['Port'] as NodeLabel[],
                // Apply port-level filters (protocol, port number)
                ...(portFilters.length > 0 && { filters: portFilters }),
                relationships: [
                  {
                    label: 'HAS_TECHNOLOGY' as const,
                    target: {
                      labels: ['Technology'] as NodeLabel[],
                      filters: [
                        {
                          field: 'key',
                          operator: '=' as Operator,
                          value: technologyKey,
                        },
                      ],
                    },
                  },
                ],
              },
            },
          ],
        },
        // Apply sorting
        ...(filters.sort && {
          orderBy: filters.sort.orderBy,
          descending: filters.sort.descending,
        }),
      },
    },
    {
      enabled: enabled && Boolean(technologyKey),
      // NOTE: Do NOT use keepPreviousData/placeholderData here.
      // When clearing a search that returned empty results, keepPreviousData
      // would show the empty placeholder while fetching, causing an empty table.
      // Instead, let TanStack Query show 'pending' status so the Loader displays.
    }
  );

  // Transform tree data to flat ImpactedAssetPort array
  const impactedAssetPorts = useMemo((): ImpactedAssetPort[] => {
    return impactedAssetsTree.flatMap(impactedAssetTree => {
      const asset = combineAssetNodeResults(impactedAssetTree)?.[0] as
        | Asset
        | undefined;
      const port = impactedAssetTree?.node?.relationships?.[0]?.target?.result
        ?.ports?.[0] as Port | undefined;

      if (!asset || !port) return [];

      return [
        {
          port,
          asset,
          visited: port.visited || '',
        },
      ];
    });
  }, [impactedAssetsTree]);

  // Client-side filtering removed - API now handles search with OR logic
  // for both identifier and group fields (see buildAssetFilters above)

  return {
    data: impactedAssetPorts,
    filters,
    setFilters,
    resetFilters,
    status,
    error,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    invalidate,
  };
}
